import { definedMap } from "@glideapps/ts-necessities";

import {
    TableGlideType,
    getTableColumn,
    ComponentKind,
    TableColumn,
    ComponentDescription,
    LegacyPropertyDescription,
    PropertyKind,
    PropertyDescription,
    TypeSchema,
    InputOutputTables,
    isSingleRelationType,
    findTable,
    MutatingScreenKind,
    TableRefGlideType,
    makeEmptyComponentDescription,
    classScreenName,
} from "../description";
import { SerializedComponent } from "../../components/SerializedApp";
import { panic } from "../../Support";
import {
    ComponentBuilder,
    ComponentDescriptor,
    ComponentErrorAndLink,
    componentErrorAndLinkForReference,
    SchemaInspector,
    InteractiveComponentConfiguratorContext,
    PropertyDescriptor,
    ComponentConfiguratorContext,
} from ".";
import { summaryColumnsForTable } from "../description-utils";
import { makeText } from "../builder-utils";
import { ComponentHandlerBase } from "./handler";
import { getColumnProperty, makeColumnProperty, makeSwitchProperty, getSwitchProperty } from "./property-utils";
import { useFallbackInitialsPropertyDescriptor, makeTitleSubtitleImagePropertyDescriptors } from "./descriptor-utils";
import { logError } from "../../debug-print";
import { ListItemFlags } from "../array-screens/list-item-flags";
import { PropertySection } from "./property-sections";
import { getDocURL } from "../../docUrl";

const ComponentKindReference: ComponentKind = "reference";

interface ReferenceComponentDescription extends ComponentDescription {
    readonly propertyName: LegacyPropertyDescription | undefined;

    readonly titleProperty: LegacyPropertyDescription | undefined;
    readonly subtitleProperty: LegacyPropertyDescription | undefined;
    readonly imageURLProperty: LegacyPropertyDescription | undefined;

    readonly useFallbackInitials?: PropertyDescription;
}

function getColumn(
    table: TableGlideType,
    desc: ReferenceComponentDescription
): [TableColumn, TableRefGlideType] | undefined {
    const columnProperty = getColumnProperty(desc.propertyName);
    if (columnProperty === undefined) return undefined;

    const column = getTableColumn(table, columnProperty);
    if (column === undefined) return undefined;

    if (!isSingleRelationType(column.type)) {
        logError("Reference column is not a table-ref");
        return undefined;
    }

    return [column, column.type];
}

function getPropertyTable(
    { input }: InputOutputTables,
    desc: ReferenceComponentDescription,
    schema: SchemaInspector
): TableGlideType | undefined {
    const column = getColumn(input, desc);
    if (column === undefined) return undefined;

    return schema.findTable(column[1]);
}

function getAllowedColumns(table: TableGlideType): readonly TableColumn[] {
    return table.columns.filter(c => isSingleRelationType(c.type));
}

export class ReferenceComponentHandler extends ComponentHandlerBase<ReferenceComponentDescription> {
    constructor() {
        super(ComponentKindReference);
    }

    public entryForTableColumn(
        builder: ComponentBuilder,
        desc: ReferenceComponentDescription,
        { input }: InputOutputTables,
        _mutatingScreenKind: MutatingScreenKind | undefined,
        _validFlag: SerializedComponent | undefined,
        _outputContext: SerializedComponent,
        _searchVariable: SerializedComponent | undefined,
        _searchNotActive: SerializedComponent | undefined
    ): SerializedComponent {
        const { b } = builder;

        const column = getColumn(input, desc);
        if (column === undefined) {
            builder.error("No column defined for reference");
            return b.emptyUI;
        }

        const columnType = column[1];
        const columnTable = builder.findTable(columnType);
        if (columnTable === undefined) return b.emptyUI;

        const ctx = builder.fetchLinkColumn(column[0], b.getProperty(column[0].name, b.getContext, input), false);

        const title = makeText(builder, desc.titleProperty, columnTable, true);
        const onTap = builder.makePushScreen(
            columnType,
            makeText(builder, desc.titleProperty, columnTable, true, true, true, false, b.getContext),
            undefined,
            b.getContext
        );

        let initials: SerializedComponent | undefined;
        if (getSwitchProperty(desc.useFallbackInitials) !== false && title !== undefined) {
            initials = b.extractInitials(title);
        }

        return b.bindContextToValue(
            b.ifNonNull(
                b.getContext,
                b.adaptiveListItem(
                    title,
                    makeText(builder, desc.subtitleProperty, columnTable, true),
                    undefined,
                    makeText(builder, desc.imageURLProperty, columnTable, true, false),
                    undefined,
                    initials,
                    onTap,
                    undefined,
                    undefined,
                    b.constant(ListItemFlags.ForceListStyle),
                    undefined,
                    undefined,
                    undefined
                )
            ),
            ctx
        );
    }

    public getDescriptor(
        _desc: ReferenceComponentDescription,
        _table: TableGlideType | undefined,
        _ccc: ComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): ComponentDescriptor<ReferenceComponentDescription> {
        const properties: PropertyDescriptor[] = [
            {
                kind: PropertyKind.Column,
                property: { name: "propertyName" },
                label: "Column",
                required: true,
                editable: true,
                searchable: false,
                getAllowedColumns,
                section: PropertySection.Data,
            },
            ...makeTitleSubtitleImagePropertyDescriptors(mutatingScreenKind, true, true, getPropertyTable),
            useFallbackInitialsPropertyDescriptor,
            ...this.getBasePropertyDescriptors(),
        ];

        return {
            name: "Relation",
            description: "A link to another part of your app",
            img: "componentRelation",
            group: "Buttons",
            helpUrl: getDocURL("reference"),
            properties,
        };
    }

    public getScreensUsed(
        desc: ReferenceComponentDescription,
        _schema: SchemaInspector,
        tables: InputOutputTables | undefined
    ): readonly string[] {
        if (tables === undefined) return [];
        const column = getColumn(tables.input, desc);
        if (column === undefined) return [];
        return [classScreenName(column[1])];
    }

    public static defaultComponent(schema: TypeSchema, column: TableColumn): ReferenceComponentDescription | undefined {
        if (!isSingleRelationType(column.type)) {
            return panic("Reference column is not a table-ref");
        }

        const desc: ReferenceComponentDescription = {
            ...makeEmptyComponentDescription(ComponentKindReference),
            propertyName: makeColumnProperty(column.name),
            titleProperty: undefined,
            subtitleProperty: undefined,
            imageURLProperty: undefined,
            useFallbackInitials: makeSwitchProperty(true),
        };

        const summary = definedMap(findTable(schema, column.type), t => summaryColumnsForTable(t, true, true, false));
        if (summary === undefined) return undefined;
        Object.assign(desc, summary);

        return desc;
    }

    // This is only here so it's possible to add this component without there being
    // a reference column.
    public newComponent(
        tables: InputOutputTables,
        usedColumns: ReadonlySet<TableColumn>,
        editedColumns: ReadonlySet<TableColumn>,
        iccc: InteractiveComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): ReferenceComponentDescription | undefined {
        const desc = super.newComponent(tables, usedColumns, editedColumns, iccc, mutatingScreenKind);
        if (desc !== undefined) return desc;

        return {
            ...makeEmptyComponentDescription(ComponentKindReference),
            propertyName: undefined,
            titleProperty: undefined,
            subtitleProperty: undefined,
            imageURLProperty: undefined,
            useFallbackInitials: makeSwitchProperty(true),
        };
    }

    public getErrorAndLink(
        desc: ReferenceComponentDescription,
        table: TableGlideType
    ): ComponentErrorAndLink | undefined {
        if (getAllowedColumns(table).length > 0 || getColumnProperty(desc.propertyName) !== undefined) {
            return undefined;
        }

        return componentErrorAndLinkForReference(table);
    }
}
